



























import {Component, Inject, Prop, Vue, Watch} from 'vue-property-decorator';
import {cloneDeep, intersectionWith} from 'lodash';
import {AIEnum} from '@/enums/AIEnum';

interface Description {
    Id: number | null;
    Description: string;
}

@Component({
    inheritAttrs: false,
})
export default class CSelect extends Vue {
    @Inject() getDescriptionsContainer!: Function;
    @Prop({required: true}) value!: number;
    @Prop({required: true}) requestApi!: string;
    @Prop({default: () => ({})}) requestQuery!: {
        [key: string]: string | number;
    };
    @Prop() additionalItems!: AIEnum;
    @Prop({default: true, type: Boolean}) showChangeState!: boolean;
    @Prop() initialValue!: number | undefined;
    @Prop() rules!: any[];
    @Prop() availableItems!: any[];
    @Prop({default: true, type: Boolean}) isRequired!: boolean;
    @Prop({default: 'plywood lighten-1', type: String}) color!: string;
    @Prop() errorKey!: string;
    @Prop() errors!: any;
    @Prop({default: false, type: Boolean}) customApi!: boolean;

    private loading: boolean = false;
    private items: Description[] = [];
    private localInitialValue: number | null = null;
    private descriptions: any = null;
    private descriptionsContainer = this.getDescriptionsContainer();
    private errorMsg: string | null = null;

    get selectClasses() {
        const classes: string[] = [];
        if (this.value != this.realInitialValue && this.showChangeState) classes.push('changed');
        return classes;
    }

    get listeners() {
        return Object.assign({}, this.$listeners, {
            input: (value: any) => this.$emit('input', value),
            change: (value: any) => this.$emit('input', value),
        });
    }

    get realInitialValue() {
        return typeof this.initialValue != 'undefined' ? this.initialValue : this.localInitialValue;
    }

    get realRules() {
        return this.rules ? this.rules : [(v: any) => Boolean(this.items.find((el: Description) => el.Id === v))];
    }

    get realAdditionalItems() {
        let result = [];
        if (!this.isRequired && this.additionalItems === undefined)
            result.push({Id: null, Description: this.$t('Common.NotSet')});
        if (!this.isRequired && this.additionalItems !== undefined) {
            result.push({Id: -1, Description: this.$t('Common.NotSet')});
            result = [...result, ...this.getAIEnumItems(this.additionalItems)];
        }
        if (this.isRequired && this.additionalItems !== undefined) {
            result = [...this.getAIEnumItems(this.additionalItems)];
        }
        return result;
    }

    @Watch('realAdditionalItems', {deep: true})
    onRealAdditionalItemsChange() {
        this.setItems();
    }
    @Watch('availableItems', {deep: true})
    onAvailablelItemsChange() {
        this.setItems();
    }

    @Watch('errors', {deep: true})
    onErrorsChange(value: any) {
        if (!this.errorKey) return console.warn('please provide error-key');
        if (this.errorKey in value) {
            this.errorMsg = value[this.errorKey][0];
        } else {
            this.errorMsg = null;
        }
    }

    @Watch('value')
    onValueChange(value: string) {
        if (this.errorMsg) this.errorMsg = null;
    }

    async created() {
        this.descriptions = await this.getDescriptions();
        this.setItems();
        this.localInitialValue = this.value;
    }

    async getDescriptions() {
        const timerId = setTimeout(() => (this.loading = true), 100);
        let request = this.customApi ? this.requestApi : `${this.requestApi}/GetDescriptions`;
        try {
            const {data} = await this.descriptionsContainer.get(request, {
                params: {
                    ...this.requestQuery,
                },
            });
            return data;
        } finally {
            clearTimeout(timerId);
            if (this.loading) this.loading = false;
        }
    }
    setItems() {
        let result: any[] = [];
        if (!this.descriptions) return;
        if (this.realAdditionalItems.length > 0)
            result = cloneDeep([...this.realAdditionalItems, ...this.descriptions]);
        else result = this.descriptions;
        if (this.availableItems && result) {
            result = intersectionWith(result, this.availableItems, comparator);
        }
        this.items = result;
    }

    getAIEnumItems(data: AIEnum | AIEnum[]) {
        const getItem = (el: AIEnum) => {
            if (el === AIEnum.NotChange) return {Id: null, Description: this.$t('Common.NotChange')};
            if (el === AIEnum.NoChoose) return {Id: null, Description: this.$t('Common.NoChoose')};
            if (el === AIEnum.NotUsed) return {Id: null, Description: this.$t('Common.NotUsed')};
            if (el === AIEnum.All) return {Id: null, Description: this.$t('Common.All')};
            if (el === AIEnum.Custom) return {Id: null, Description: this.$t('Common.Custom')};
            return null;
        };
        if (Array.isArray(data)) {
            return data.map(getItem);
        } else {
            return [getItem(data)];
        }
    }

    /**
     * @public
     */
    getItems() {
        return new Promise(async resolve => {
            const value = await this.getDescriptions();
            resolve(value);
        });
    }
}

const comparator = (a: any, b: any): boolean => a.Id === b;
